// Configurações › Negociação (specs/21): liga/desliga o módulo e monta o processo da empresa.
import type { PedeValor } from '@crm/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon, Toast, useToast } from '../components/ui';
import { COR_GANHO, COR_PERDIDO, fundoEtapa, MODELOS, useConfigNegociacao, type ConfigNegociacao, type EtapaForm } from '../negociacao';

const CORES = ['#A78BFA', '#F59E0B', '#60A5FA', '#2DD4BF', '#FB923C', '#E879F9', '#94A3B8', '#F472B6', '#38BDF8', '#A3E635', '#FCD34D', '#C084FC'];

interface Form { ativa: boolean; etapas: EtapaForm[]; ganhoNome: string; perdidoNome: string; motivos: string[] }
const doServidor = (c: ConfigNegociacao): Form => ({
  ativa: c.ativa,
  etapas: c.etapas.map((e) => ({
    id: e.id, nome: e.nome, pedeQuantidade: e.pedeQuantidade, rotuloQuantidade: e.rotuloQuantidade ?? '', pedeValor: e.pedeValor,
    rotuloValor: e.rotuloValor ?? '', prazoDias: e.prazoDias ? String(e.prazoDias) : '', somaPipeline: e.somaPipeline,
  })),
  ganhoNome: c.ganho?.nome ?? 'Ganho', perdidoNome: c.perdido?.nome ?? 'Perdido', motivos: c.motivos.map((m) => m.nome),
});

export function NegociacaoConfig() {
  const { pode } = useAuth();
  const admin = pode('CONFIGURACOES_GERENCIAR');
  const qc = useQueryClient();
  const toast = useToast();
  const q = useConfigNegociacao();
  const [f, setF] = useState<Form | null>(null);
  const [modelo, setModelo] = useState<string | null>(null);
  const [novoMotivo, setNovoMotivo] = useState('');
  const [prev, setPrev] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { if (q.data && !f) setF(doServidor(q.data)); }, [q.data, f]);

  if (q.isLoading || (!f && !q.error)) return <Carregando />;
  if (q.error) return <Erro erro={mensagemErro(q.error)} />;
  const form = f!;
  const set = (p: Partial<Form>) => setF({ ...form, ...p });
  const etapa = (i: number, p: Partial<EtapaForm>) => set({ etapas: form.etapas.map((e, j) => (j === i ? { ...e, ...p } : e)) });
  const mover = (i: number, d: -1 | 1) => { const l = [...form.etapas]; [l[i], l[i + d]] = [l[i + d]!, l[i]!]; set({ etapas: l }); };
  const alterado = JSON.stringify(form) !== JSON.stringify(doServidor(q.data!));

  function aplicarModelo(k: string) {
    setModelo(k);
    // mantém os ids das etapas atuais nas primeiras posições (negociações abertas continuam nelas)
    const novas = MODELOS[k]!.etapas().map((e, i) => ({ ...e, id: form.etapas[i]?.id }));
    set({ etapas: novas });
    setPrev(0);
  }
  async function salvar() {
    setErro(null);
    setSalvando(true);
    try {
      const r = await api<ConfigNegociacao>('/config/negociacao', {
        method: 'PUT',
        body: {
          ativa: form.ativa, ganhoNome: form.ganhoNome.trim(), perdidoNome: form.perdidoNome.trim(), motivos: form.motivos,
          etapas: form.etapas.map((e) => ({
            ...(e.id ? { id: e.id } : {}), nome: e.nome.trim(), pedeQuantidade: e.pedeQuantidade,
            rotuloQuantidade: e.pedeQuantidade ? e.rotuloQuantidade.trim() || null : null, pedeValor: e.pedeValor,
            rotuloValor: e.pedeValor === 'NAO' ? null : e.rotuloValor.trim() || null,
            prazoDias: e.prazoDias ? Number(e.prazoDias) : null, somaPipeline: e.pedeValor !== 'NAO' && e.somaPipeline,
          })),
        },
      });
      qc.setQueryData(['config-negociacao'], r);
      setF(doServidor(r));
      setModelo(null);
      await qc.invalidateQueries({ queryKey: ['negociacoes'] });
      toast.show('Configuração de negociação salva.');
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setSalvando(false);
    }
  }

  // prévia do "Registrar contato"
  const opcoes = [
    { nome: 'Só contato', cor: null as string | null, campos: [] as string[], info: 'Registra só o contato.' },
    ...form.etapas.map((e, i) => ({
      nome: e.nome || 'Sem nome', cor: CORES[i % CORES.length]!,
      campos: [
        ...(e.pedeQuantidade ? [`${e.rotuloQuantidade || 'Quantidade'} *`] : []),
        ...(e.pedeValor !== 'NAO' ? [`${e.rotuloValor || 'Valor'}${e.pedeValor === 'OBRIGATORIO' ? ' *' : ' (opcional)'}`] : []),
        ...(e.prazoDias ? [e.pedeValor === 'NAO' ? `Cobrar retorno em ${e.prazoDias} dias` : `Válido até (${e.prazoDias} dias)`] : []),
      ],
      info: e.pedeValor !== 'NAO' && e.somaPipeline ? 'Conta em “R$ em aberto”, não em vendas.' : 'Não soma no pipeline nem em vendas.',
    })),
    { nome: form.ganhoNome || 'Ganho', cor: COR_GANHO, campos: ['Valor vendido *', 'Data da venda *'], info: 'Entra em vendas na data da venda.' },
    { nome: form.perdidoNome || 'Perdido', cor: COR_PERDIDO, campos: ['Valor que deixou de vender', 'Motivo *'], info: 'Não entra em vendas; conta em “Perdido”.' },
  ];
  const op = opcoes[Math.min(prev, opcoes.length - 1)]!;

  return (
    <div className="col" style={{ gap: 18, maxWidth: 1180 }}>
      <section className="card" aria-labelledby="h-neg-liga">
        <div className="row" style={{ gap: 16, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
          <div className="col" style={{ gap: 4, flex: 1 }}>
            <h2 id="h-neg-liga" style={{ fontSize: 18 }}>Usar negociações</h2>
            <span className="small muted">Acompanha cada negócio do primeiro passo até ganhar ou perder: o que está em aberto, quanto foi perdido e por quê.</span>
          </div>
          <button type="button" role="switch" aria-checked={form.ativa} aria-labelledby="h-neg-liga" className={`switch${form.ativa ? ' on' : ''}`}
            disabled={!admin} onClick={() => set({ ativa: !form.ativa })} />
        </div>
        {!form.ativa && (
          <div className="alert info"><Icon name="alerta" /><span>Desligado: o “Registrar contato” mostra só “Valor vendido (se vendeu)” e o menu Negociações não aparece. As vendas continuam contando no Dashboard. Negociações já registradas ficam guardadas e voltam se você ligar de novo.</span></div>
        )}
      </section>

      <div className="col" style={{ gap: 18, opacity: form.ativa ? 1 : 0.45, pointerEvents: form.ativa && admin ? undefined : 'none' }} aria-disabled={!form.ativa}>
        <section className="card" aria-labelledby="h-neg-mod">
          <div className="col" style={{ gap: 2 }}>
            <h2 id="h-neg-mod" style={{ fontSize: 16 }}>Começar de um modelo</h2>
            <span className="small muted">Monta as etapas abaixo. Depois você ajusta do jeito da sua empresa.</span>
          </div>
          <div className="modelos-neg">
            {Object.entries(MODELOS).map(([k, m]) => (
              <button key={k} type="button" className="modelo-neg" aria-pressed={modelo === k} onClick={() => aplicarModelo(k)}>
                <strong style={{ fontSize: 13.5 }}>{m.nome}</strong><span className="small faint">{m.desc}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="card" aria-labelledby="h-neg-et">
          <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
            <div className="col" style={{ gap: 2, flex: '1 1 320px' }}>
              <h2 id="h-neg-et" style={{ fontSize: 16 }}>Etapas do processo</h2>
              <span className="small muted">As etapas em aberto vêm antes do resultado. A cor é automática, pela posição.</span>
            </div>
            <button className="btn" disabled={form.etapas.length >= 12}
              onClick={() => set({ etapas: [...form.etapas, { nome: 'Nova etapa', pedeQuantidade: false, rotuloQuantidade: '', pedeValor: 'OPCIONAL', rotuloValor: 'Valor', prazoDias: '7', somaPipeline: true }] })}>
              <Icon name="mais" size={14} />Adicionar etapa
            </button>
          </div>
          <div className="row" style={{ gap: 8, alignItems: 'center' }} aria-label="Resumo do processo">
            {form.etapas.map((e, i) => (
              <span key={i} className="row" style={{ gap: 8, alignItems: 'center' }}>
                <span className="pilula-neg" style={{ background: fundoEtapa(CORES[i % CORES.length]!), color: CORES[i % CORES.length] }}>{e.nome || 'Sem nome'}</span>
                <span className="faint" aria-hidden="true">→</span>
              </span>
            ))}
            <span className="pilula-neg" style={{ background: 'var(--surface-3)', color: 'var(--text)' }}>{form.ganhoNome} / {form.perdidoNome}</span>
          </div>

          {form.etapas.map((e, i) => (
            <div key={e.id ?? `n${i}`} className="etapa-neg">
              <span className="etapa-neg-n" style={{ background: CORES[i % CORES.length] }}>{i + 1}</span>
              <label className="field"><span>Nome</span>
                <input className="input" maxLength={30} value={e.nome} onChange={(ev) => etapa(i, { nome: ev.target.value })} />
              </label>
              <div className="field">
                <span className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
                  <button type="button" role="switch" aria-checked={e.pedeQuantidade} aria-label="Pede quantidade" className={`switch sm${e.pedeQuantidade ? ' on' : ''}`}
                    onClick={() => etapa(i, { pedeQuantidade: !e.pedeQuantidade })} />Pede quantidade
                </span>
                <input className="input" placeholder="Ex.: Peças enviadas" maxLength={40} disabled={!e.pedeQuantidade} value={e.rotuloQuantidade}
                  aria-label="Nome da quantidade" onChange={(ev) => etapa(i, { rotuloQuantidade: ev.target.value })} />
              </div>
              <div className="field"><span>Valor</span>
                <span className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
                  <select className="select" style={{ width: 132, flex: 'none' }} value={e.pedeValor} aria-label="Pede valor"
                    onChange={(ev) => { const v = ev.target.value as PedeValor; etapa(i, { pedeValor: v, somaPipeline: v !== 'NAO' }); }}>
                    <option value="NAO">Não pede</option><option value="OPCIONAL">Opcional</option><option value="OBRIGATORIO">Obrigatório</option>
                  </select>
                  <input className="input" placeholder="Ex.: Valor do orçamento" maxLength={40} disabled={e.pedeValor === 'NAO'} value={e.rotuloValor}
                    aria-label="Nome do valor" onChange={(ev) => etapa(i, { rotuloValor: ev.target.value })} />
                </span>
              </div>
              <label className="field"><span>Prazo para cobrar</span>
                <span className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
                  <input className="input num" style={{ width: 76 }} inputMode="numeric" value={e.prazoDias}
                    onChange={(ev) => etapa(i, { prazoDias: ev.target.value.replace(/\D/g, '').slice(0, 3) })} /><span className="small faint">dias</span>
                </span>
                <span className="hint">{e.prazoDias ? `Depois disso aparece como ${e.pedeValor === 'NAO' ? '“sem retorno”' : '“vencido”'}` : 'Sem prazo'}</span>
              </label>
              <div className="field">
                <span className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
                  <button type="button" role="switch" aria-checked={e.pedeValor !== 'NAO' && e.somaPipeline} aria-label="Soma no pipeline" disabled={e.pedeValor === 'NAO'}
                    className={`switch sm${e.pedeValor !== 'NAO' && e.somaPipeline ? ' on' : ''}`} onClick={() => etapa(i, { somaPipeline: !e.somaPipeline })} />Soma no pipeline
                </span>
                <span className="hint">{e.pedeValor === 'NAO' ? 'Só para etapas com valor' : e.somaPipeline ? 'Conta em “R$ em aberto”' : 'Não conta no pipeline'}</span>
              </div>
              <div className="row" style={{ gap: 4, justifyContent: 'flex-end', flexWrap: 'nowrap', marginTop: 22 }}>
                <button className="btn sm icon" aria-label={`Subir ${e.nome}`} disabled={i === 0} onClick={() => mover(i, -1)}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 15l-6-6-6 6" /></svg></button>
                <button className="btn sm icon" aria-label={`Descer ${e.nome}`} disabled={i === form.etapas.length - 1} onClick={() => mover(i, 1)}><Icon name="abaixo" size={13} /></button>
                <button className="btn sm icon" aria-label={`Remover ${e.nome}`} disabled={form.etapas.length === 1}
                  onClick={() => set({ etapas: form.etapas.filter((_, j) => j !== i) })}><Icon name="fechar" size={13} /></button>
              </div>
            </div>
          ))}

          <div className="grid-2-neg">
            <label className="field etapa-neg-final"><span>Resultado ganho · entra em vendas, pede valor e data da venda</span>
              <input className="input" maxLength={30} value={form.ganhoNome} onChange={(ev) => set({ ganhoNome: ev.target.value })} /></label>
            <label className="field etapa-neg-final"><span>Resultado perdido · não entra em vendas, pede motivo</span>
              <input className="input" maxLength={30} value={form.perdidoNome} onChange={(ev) => set({ perdidoNome: ev.target.value })} /></label>
          </div>
          <span className="small faint">Ao remover uma etapa, as negociações abertas nela passam para a primeira etapa. Mudar o que uma etapa pede vale daqui para frente; o histórico não muda.</span>
        </section>

        <section className="card" aria-labelledby="h-neg-mot">
          <div className="col" style={{ gap: 2 }}>
            <h2 id="h-neg-mot" style={{ fontSize: 16 }}>Motivos de perda</h2>
            <span className="small muted">O vendedor escolhe um ao marcar como perdido. Aparecem no relatório de perdas.</span>
          </div>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            {form.motivos.map((m, i) => (
              <span key={m} className="chip-neg">{m}
                <button type="button" aria-label={`Remover motivo ${m}`} disabled={form.motivos.length === 1}
                  onClick={() => set({ motivos: form.motivos.filter((_, j) => j !== i) })}><Icon name="fechar" size={11} /></button>
              </span>
            ))}
            <input className="input" style={{ width: 200 }} maxLength={60} placeholder="Novo motivo" value={novoMotivo} onChange={(e) => setNovoMotivo(e.target.value)} aria-label="Novo motivo" />
            <button className="btn sm" disabled={!novoMotivo.trim() || form.motivos.some((m) => m.toLowerCase() === novoMotivo.trim().toLowerCase())}
              onClick={() => { set({ motivos: [...form.motivos, novoMotivo.trim()] }); setNovoMotivo(''); }}>Adicionar</button>
          </div>
        </section>

        <section className="card" aria-labelledby="h-neg-prev">
          <div className="col" style={{ gap: 2 }}>
            <h2 id="h-neg-prev" style={{ fontSize: 16 }}>Como o vendedor vê no “Registrar contato”</h2>
            <span className="small muted">Clique nas opções para ver o que cada etapa pede.</span>
          </div>
          <div className="prev-neg">
            <div className="row" style={{ gap: 6 }} role="group" aria-label="Prévia">
              {opcoes.map((o, i) => (
                <button key={i} type="button" className="opt-neg" aria-pressed={prev === i} onClick={() => setPrev(i)}
                  style={prev === i && o.cor ? { borderColor: o.cor, color: o.cor, background: fundoEtapa(o.cor) } : undefined}>{o.nome}</button>
              ))}
            </div>
            {op.campos.length > 0 && <div className="row" style={{ gap: 12 }}>{op.campos.map((c) => <label key={c} className="field" style={{ flex: '1 1 200px', maxWidth: 280 }}><span>{c}</span><input className="input" disabled /></label>)}</div>}
            <span className="small faint">{op.info}</span>
          </div>
        </section>
      </div>

      <Erro erro={erro} />
      {admin ? (
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" disabled={!alterado || salvando} onClick={() => { setF(doServidor(q.data!)); setModelo(null); }}>Descartar alterações</button>
          <button className="btn primary" disabled={!alterado || salvando} onClick={() => void salvar()}>{salvando ? 'Salvando…' : 'Salvar'}</button>
        </div>
      ) : <span className="small muted">Só o administrador altera a negociação.</span>}
      <Toast msg={toast.msg} onDone={toast.clear} />
    </div>
  );
}
